import React, { useState, useMemo } from 'react';
import SingleOTPInput from './SingleOTPInput';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { BodyS, BodySBold } from 'components/Typography/Typography';
import Timer from 'components/Timer/Timer';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/components/otpStyle';
import { makeStyles } from '@material-ui/core/styles';
import TimerWithResend from 'components/Timer/TimerWithResend';
import { useSelector } from 'react-redux';
import { getOTPScreenLabels } from 'redux/modules/dynamicLabels/selectors';

// @ts-ignore
const useStyles = makeStyles(styles);

const OTP_LENGTH = 5;
const EXPIRY_TIME = 3;

const replaceAt = (str: string, index: number, value: string): string => {
	const array = str.split('');
	array[index] = value;
	return array.join('');
};

interface IOtpProps {
	otpValue: string;
	setOtpValue: (value: string) => void;
	otpIncorrect?: boolean;
	showResendOption?: boolean;
	isNewDashBoardEnabled?: boolean;
	resendOtp?: () => void;
	expire_time?: number;
	submitOTP?: () => void;
}

const OTP = ({
	otpValue,
	setOtpValue,
	otpIncorrect,
	showResendOption,
	isNewDashBoardEnabled,
	resendOtp = () => {},
	expire_time = EXPIRY_TIME,
	submitOTP,
}: IOtpProps) => {
	const classes = useStyles({ isNewDashBoardEnabled });
	const [activeInput, setActiveInput] = useState(0);
	const [tokenExpired, setTokenExpired] = useState(false);
	const otpLabels = useSelector(getOTPScreenLabels);

	const updateOtpValue = (value: string) => {
		const _optValue = replaceAt(otpValue, activeInput, value);
		setOtpValue(_optValue);
	};

	const focusOnNextInput = (): void => {
		setActiveInput((activeInput) => Math.min(activeInput + 1, OTP_LENGTH - 1));
	};

	const focusOnPrevInput = (): void => {
		setActiveInput((activeInput) => Math.max(activeInput - 1, 0));
	};

	const handleOnChange = (event: React.ChangeEvent<{ value: string }>): void => {
		event.persist();
		const value = event.target.value.replace(/[^0-9]+/g, '');
		if (value.length > 0) {
			updateOtpValue(value);
		} else {
			focusOnPrevInput();
		}
	};

	const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
		switch (event.key) {
			case 'Backspace':
				event.preventDefault();
				focusOnPrevInput();
				updateOtpValue('');
				break;
			case 'ArrowLeft':
				event.preventDefault();
				focusOnPrevInput();
				break;
			case 'ArrowRight':
				event.preventDefault();
				focusOnNextInput();
				break;
			case 'Enter':
				event.preventDefault();
				if (otpValue.length === 5 && submitOTP) {
					setActiveInput(0);
					submitOTP();
				}
				break;
			default:
				break;
		}
	};

	const handleFocus = (event: React.FocusEvent<HTMLInputElement>, index: number): void => {
		setActiveInput(index);
		event.target.select();
	};

	const handleOnInput = (event: React.FormEvent<HTMLInputElement>) => {
		event.persist();
		event.preventDefault();
		focusOnNextInput();
	};

	const renderInputs = useMemo(() => {
		const inputs = [];
		for (let index = 0; index < OTP_LENGTH; index++) {
			inputs.push(
				<SingleOTPInput
					key={index}
					focus={index === activeInput}
					value={otpValue[index]}
					onChange={handleOnChange}
					onKeyDown={handleOnKeyDown}
					handleFocus={(event: React.FocusEvent<HTMLInputElement>) => handleFocus(event, index)}
					onInput={handleOnInput}
					error={tokenExpired || !!otpIncorrect}
					isNewDashBoardEnabled={isNewDashBoardEnabled}
				/>
			);
		}
		return inputs;
		// eslint-disable-next-line
	}, [activeInput, otpValue, tokenExpired, otpIncorrect]);

	return (
		<div>
			<div className={classes.otpWrapper}>{renderInputs}</div>
			<div>
				<ConditionalRender
					show={(tokenExpired || !!otpIncorrect) && !showResendOption}
					onTrue={() => (
						<BodyS color="danger" inline isNewDashboardEnabled={isNewDashBoardEnabled}>
							{tokenExpired
								? translate('active.services.add.billing.account.password.expired')
								: isNewDashBoardEnabled
								? otpLabels.wrongOTP
								: translate('active.services.add.billing.account.incorrect.password')}
						</BodyS>
					)}
				/>
				<br />

				<ConditionalRender
					show={!tokenExpired}
					onTrue={() => (
						<>
							{isNewDashBoardEnabled ? (
								<TimerWithResend onResend={resendOtp} otpLabels={otpLabels} />
							) : (
								<>
									<BodyS inline>
										{translate('active.services.add.billing.account.your.password.valid.for')}
									</BodyS>{' '}
									<BodySBold inline>
										<Timer minutes={expire_time} whenExpired={() => setTokenExpired(true)} />
									</BodySBold>
								</>
							)}
						</>
					)}
				/>
			</div>
		</div>
	);
};

export default OTP;
